import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ExternalLink } from "@/components/ExternalLink";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";

const styles = {
  root: {
    padding: "14px 16px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "10px",
    backgroundColor: "#fbfcff",
  },
  checkboxLabel: {
    margin: 0,
    alignItems: "center",
    gap: "10px",
    "& .MuiFormControlLabel-label": {
      fontSize: "12px",
      color: "#46556f",
    },
    "& .MuiCheckbox-root": {
      padding: "4px",
    },
  },
  warningBanner: {
    mt: "14px",
    padding: "10px 12px",
    borderRadius: "8px",
    backgroundColor: "#fff6e8",
    border: "1px solid #f3d7a6",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface ComplianceGateProps {
  values: [boolean, boolean, boolean];
  onChange: (index: number, checked: boolean) => void;
}

export const ComplianceGate: React.FC<ComplianceGateProps> =
  function ComplianceGate({ values, onChange }) {
    const { t } = useTranslation();
    const allChecked = useMemo(() => values.every(Boolean), [values]);

    return (
      <Box>
        <Typography variant="h3" fontWeight={700}>
          {t("emailOnboard.domainSetup.complianceGate.title", "建立前須確認")}
        </Typography>
        <Stack sx={styles.root} gap="10px" mt="12px">
          <FormControlLabel
            sx={styles.checkboxLabel}
            control={
              <Checkbox
                checked={values[0]}
                onChange={(_, checked) => onChange(0, checked)}
                size="small"
                sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
              />
            }
            label={
              <Stack direction="row" alignItems="center" gap="4px">
                <Box component="span">
                  {t(
                    "emailOnboard.complianceGate.dnsPermission",
                    "我已確認，擁有寄件網域的 DNS 設定權限",
                  )}
                </Box>
                <Tooltip
                  title={t(
                    "emailOnboard.complianceGate.dnsPermission.tooltip",
                    "DNS 是管理網域設定的系統，例如 Cloudflare、GoDaddy。若你不確定自己是否有 DNS 權限，建議先和 IT 或工程師確認，否則後續網域驗證與 Email 開通可能會失敗。",
                  )}
                >
                  <Box component="span" sx={{ display: "flex" }}>
                    <MotifIcon
                      un-i-motif="circle_info"
                      style={{
                        fontSize: 14,
                        color: theme.colors.staticFgNote,
                      }}
                    />
                  </Box>
                </Tooltip>
              </Stack>
            }
          />
          <FormControlLabel
            sx={styles.checkboxLabel}
            control={
              <Checkbox
                checked={values[1]}
                onChange={(_, checked) => onChange(1, checked)}
                size="small"
                sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
              />
            }
            label={t(
              "emailOnboard.complianceGate.listConsent",
              "我已確認 Email 名單皆取得收件者同意，並遵守平台發信政策",
            )}
          />
          <FormControlLabel
            sx={styles.checkboxLabel}
            control={
              <Checkbox
                checked={values[2]}
                onChange={(_, checked) => onChange(2, checked)}
                size="small"
                sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
              />
            }
            label={
              <Stack
                direction="row"
                alignItems="center"
                gap="4px"
                flexWrap="wrap"
              >
                <Box component="span">
                  <Trans
                    i18nKey="emailOnboard.complianceGate.policyAgreement"
                    defaults="我已詳閱 <agreement>EDM 電子郵件行銷服務使用規範</agreement> 與計費方式"
                    components={{
                      agreement: (
                        <ExternalLink
                          href="https://drive.google.com/file/d/18X3aggG2c52V4egV6BOna7VOXMEDz4kk/view?usp=sharing"
                          trailingIcon
                        />
                      ),
                    }}
                  />
                </Box>
              </Stack>
            }
          />
        </Stack>
        {!allChecked ? (
          <Box sx={styles.warningBanner}>
            <Typography variant="body" fontWeight={600} color="#9b6a1e">
              {t(
                "emailOnboard.complianceGate.hint",
                "請先勾選上方 3 項確認事項，才能填寫 Email 網域資料。",
              )}
            </Typography>
          </Box>
        ) : null}
      </Box>
    );
  };
